import React, { useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import api from "../../api/api";
import DeleteConfirmModal from "../../Reports/deleconformation";
import { getPracticeId } from "../../config/practice";

/*
 * The visit's order notes on a dashboard card, each with a delete (OSA's
 * card, practice profile card.orderNotes: 7825). A note is an orders row with
 * ordertype "notes"; the text is its ordername. Delete is v1 DELETE
 * /orders/delete/multiple for that one id, dated by the visit (the server
 * picks orders_master or its history table from the date -- the date the
 * dashboard loaded the card with, so its table is the one the note came
 * from even after midnight), and on success
 * the row is dropped from the dashboard list in place.
 */
export const orderNotesOf = (row) =>
  (Array.isArray(row?.orders) ? row.orders : [])
    .filter((o) => String(o?.ordertype || "").toLowerCase() === "notes" && String(o?.ordername || "").trim())
    .map((o) => ({ id: o.id, note: String(o.ordername).trim(), by: o.user_role || null }));

// The delete flow shared by the Order Notes list and the card view's notes
// box: pendingId (the note awaiting confirmation), busy, and confirmDelete.
function useOrderNoteDelete({ data, setFetchedData, setToastMessage, setToastSeverity, setOpenToast }) {
  const [pendingId, setPendingId] = useState(null);
  const [busy, setBusy] = useState(false);
  // The dashboard's shown date: its list (and these orders) were read for it.
  const loadedDate = useSelector((state) => state.filter?.currentDate);

  const toast = (message, severity) => {
    setToastMessage?.(message);
    setToastSeverity?.(severity);
    setOpenToast?.(true);
  };

  const confirmDelete = async () => {
    if (!pendingId) return;
    const id = pendingId;
    setBusy(true);
    try {
      const response = await api.delete(`/v1/${getPracticeId()}/orders/delete/multiple`, {
        // patientid + ordertype: the server deletes only this patient's note (7825's
        // orders_master / _history ids collide, so an id + date alone is not enough).
        data: {
          ordersArray: [id],
          orderdate: loadedDate || dayjs(data?.startdate).format("YYYY-MM-DD"),
          patientid: Number(data?.patientid),
          ordertype: "notes",
        },
      });
      if (response?.status === 200) {
        setFetchedData?.((prev) =>
          (prev || []).map((item) =>
            item.id === data.id ? { ...item, orders: (item.orders || []).filter((o) => o.id !== id) } : item,
          ),
        );
        toast("Note deleted successfully", "success");
      } else {
        toast(response?.message || "Failed to delete note", "error");
      }
    } catch (error) {
      toast("Something went wrong", "error");
    } finally {
      setBusy(false);
      setPendingId(null);
    }
  };

  return { pendingId, setPendingId, busy, confirmDelete };
}

const DeleteNoteConfirm = ({ pendingId, setPendingId, busy, confirmDelete }) => (
  <DeleteConfirmModal
    open={Boolean(pendingId)}
    onClose={() => setPendingId(null)}
    onConfirm={confirmDelete}
    loading={busy}
    title="Delete Note"
    message="Are you sure you want to delete this note? This action cannot be undone."
  />
);

export default function OrderNotes(props) {
  const del = useOrderNoteDelete(props);
  const { setPendingId } = del;
  const notes = orderNotesOf(props.data);

  return (
    <div className="w-full mt-2" data-testid="order-notes">
      <div className="text-[#6B7280] font-[600] font-inter text-[13px] mb-1">Order Notes</div>
      {notes.length === 0 ? (
        <div className="text-[11px] font-inter text-[#94A3B8] italic">No order notes</div>
      ) : (
        <div className="flex flex-col gap-1 border-l-2 border-[#1D2951] pl-2 max-h-[84px] overflow-y-auto">
          {notes.map((entry) => (
            <div key={entry.id} className="flex items-start justify-between gap-1 text-left">
              <p className="text-[11px] font-inter text-[#374151] flex-1 break-words whitespace-normal my-0">
                • {entry.note}
                {entry.by && <span className="text-[#9CA3AF] ml-1">({entry.by})</span>}
              </p>
              {entry.id && (
                <button
                  type="button"
                  onClick={() => setPendingId(entry.id)}
                  className="text-[#9CA3AF] hover:text-red-500 text-[13px] leading-none flex-shrink-0"
                  title="Delete note"
                  aria-label="Delete note"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <DeleteNoteConfirm {...del} />
    </div>
  );
}

// The appointment note as the card view writes it: <br> tags dropped, first
// letter capitalised, "Notes: " in front (Athena's "Test(s) Due:" becomes
// "Notes:"); cut at CARD_NOTE_LIMIT characters unless `showMore`. `long`
// (there is more to show) comes from the same text as the cut, so a note cut
// with "..." always gets its Show more -- the old test used the raw note,
// which left notes of 74-80 characters cut with no way to read the end.
export const CARD_NOTE_LIMIT = 80;
export function cardNoteView(notes, showMore) {
  const cleaned = String(notes || "").replace(/<br\s*\/?>/gi, "").trim();
  let full = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  full = /^Test\(s\) Due:/i.test(full) ? full.replace(/^Test\(s\) Due:/i, "Notes:") : `Notes: ${full}`;
  const long = full.length > CARD_NOTE_LIMIT;
  return { text: long && !showMore ? full.slice(0, CARD_NOTE_LIMIT) + "..." : full, long };
}

// The card view's Notes as OSA's build drew it (practice cardNotesBox(),
// 7825): one 84px scroll box with the whole appointment note and the visit's
// order notes as bullets (each with its delete), "No notes" when both are
// empty. It replaces the cut "Notes:" line and the separate Order Notes list.
export function CardNotesBox(props) {
  const del = useOrderNoteDelete(props);
  const { setPendingId } = del;
  const { data } = props;
  const apptNote = String(data?.notes || "").replace(/<br\s*\/?>/gi, "").trim();
  const notes = orderNotesOf(data);

  return (
    <div className="flex flex-col gap-1 w-full text-start my-2" data-testid="card-notes-box">
      <div className="bg-[#F9FAFB] rounded-md p-2.5 relative h-[84px] max-h-[84px] overflow-y-auto custom-scrollbar">
        <span className="font-normal w-full flex font-inter text-xs text-[#94A3B8] mb-1">Notes</span>
        {apptNote || notes.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            {apptNote && (
              <div className="flex text-left items-start gap-1 border-l-2 border-[#1D2951] pl-2">
                <span className="text-[11px] font-inter font-normal text-[#1D2951] leading-normal flex-1 break-words whitespace-normal">
                  • {apptNote}
                </span>
              </div>
            )}
            {notes.length > 0 && (
              <div className="border-l-2 border-[#1D2951] pl-2" data-testid="order-notes">
                {notes.map((entry) => (
                  <div key={entry.id} className="flex items-start justify-between gap-1 text-left">
                    <p className="text-[11px] font-inter font-normal mt-0.5 leading-normal text-[#374151] flex-1 break-words whitespace-normal my-0">
                      • {entry.note}
                      {entry.by && <span className="text-[#9CA3AF] ml-1">({entry.by})</span>}
                    </p>
                    {entry.id && (
                      <button
                        type="button"
                        onClick={() => setPendingId(entry.id)}
                        className="text-[#9CA3AF] hover:text-red-500 text-[13px] leading-none mt-0.5 flex-shrink-0"
                        title="Delete note"
                        aria-label="Delete note"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span className="text-[11px] font-inter font-normal text-[#94A3B8] italic">No notes</span>
        )}
      </div>
      <DeleteNoteConfirm {...del} />
    </div>
  );
}
